// Closeness for the pricing screen (Tim, 30 Sep: "The pricing should be a little more robust"). Every piece the store has sold,
// every market check Shane has added and every similar piece still on the rack is scored against the piece being priced, out of
// 100: same brand 30, same type 30 (another kind of top 15), the words that name the model 25 (rare words count for more, so
// "floral dagger" or "Soundgarden" matter and "vintage" or "faded" hardly do), price 10, size 5. The closer a match, the more it
// weighs in the price and the time to sell. Pure functions, so the arithmetic can be checked on its own.

export const TOPS = ['Tees', 'Long sleeves', 'Shirts']
const SIZED = new Set([...TOPS, 'Hoodies & Sweaters', 'Outerwear', 'Pants & Denim', 'Shorts', 'Shoes'])
export const CLOSE = 55, NEAR = 40
export const NO_BRAND = new Set(['No brand', 'Vintage'])

const STOP = new Set(['the', 'a', 'an', 'of', 'and', 'in', 'on', 'with', 'x', 'by', 'for', 'to', 'de', 'la', 'le', 'les', 'du', 'di', 'w', 'sz', 'size', 'new', 'nwt', 'og', 'from', 'is', 'it', 'its', 'my', 'no', 'not', 'at'])
// words that only say what kind of piece it is: the type already counts for that
const GENERIC = new Set(['tee', 'tees', 't', 'shirt', 'shirts', 'tshirt', 'tshirts', 'long', 'sleeve', 'sleeves', 'longsleeve', 'ls', 'top', 'tops', 'piece'])
const BRAND_WORDS: Record<string, string[]> = {
  'chrome hearts': ['ch', 'chrome', 'hearts'],
  'enfants riches deprimes': ['erd', 'enfants', 'riches', 'deprimes'],
  'paly hollywood': ['paly', 'hollywood'],
  'vintage': ['vintage'],
}
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
function brandWords(brand: string): Set<string> {
  const b = fold(brand)
  return new Set([...b.replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean), ...(BRAND_WORDS[b.replace(/[^a-z0-9 ]+/g, '').trim()] ?? [])])
}
/** The words in a title that can name a model: brand words, filler and type words taken out. */
export function wordsOf(title: string, brand: string): string[] {
  const bw = brandWords(brand)
  const ws = fold(title).replace(/['’]/g, '').replace(/[^a-z0-9]+/g, ' ').trim().split(' ')
  return [...new Set(ws.filter((w) => w && !STOP.has(w) && !GENERIC.has(w) && !bw.has(w) && (w.length > 1 || /\d/.test(w))))]
}
/** How rare each word is across everything that can be compared (rare words say more about the model). */
export function rarity(docs: string[][]) {
  const df = new Map<string, number>()
  for (const d of docs) for (const w of d) df.set(w, (df.get(w) ?? 0) + 1)
  const N = Math.max(1, docs.length)
  return (w: string) => Math.max(0.1, Math.log(N / (df.get(w) ?? 1)))
}

export interface Target { pid: string; brand: string; type: string; cat?: string[] | null; size: string | null; condition: string | null; words: string[]; anchor: number | null }
export interface Cand {
  key: string; kind: 'sold' | 'market' | 'floor'; pid: string | null; title: string; brand: string; type: string; cat?: string[] | null; size: string | null; condition: string | null
  list: number | null; price: number | null; days: number | null; entry: number; when: string | null; image: string | null; words: string[]
  marked?: boolean; hist?: boolean; source?: string; url?: string | null; mkind?: string; id?: number; note?: string | null; for_pid?: string | null
}
export interface Scored extends Cand { score: number; why: string[]; w: number }

/** The same kind of piece: by the store's Category field (custom.category) when both have it, so a tee is only matched with tees and
 * a hoodie with hoodies (Tim, 20 Oct); by Type when either has no category. */
export function sameKind(p: { type: string; cat?: string[] | null }, c: { type: string; cat?: string[] | null }) {
  if (p.cat?.length && c.cat?.length) return p.cat.some((x) => c.cat!.includes(x))
  return p.type === c.type
}
// a tee or shirt can lean on the other when too few of its own have sold, never on a hoodie, sweater or jacket (a "long sleeve"
// thermal is filed as a sweater: its Category says so even where its title reads like a top)
const LIGHT_TOPS = ['T-shirts', 'Shirts', 'Tank Tops']
const HEAVY = ['Hoodies', 'Sweaters/Sweatshirts', 'Jackets', 'Vests']
const lightTop = (x: { type: string; cat?: string[] | null }) => x.cat?.length ? x.cat.some((c) => LIGHT_TOPS.includes(c)) && !x.cat.some((c) => HEAVY.includes(c)) : TOPS.includes(x.type)
/** Looser, only when too few of the same kind have sold: a tee or shirt for a tee or shirt. */
export const sameFamily = (p: { type: string; cat?: string[] | null }, c: { type: string; cat?: string[] | null }) => sameKind(p, c) || (lightTop(p) && lightTop(c))

const pretty = (w: string) => w.replace(/^\w/, (c) => c.toUpperCase())
/** A candidate's score out of 100 and the reasons, for the chips on each comparison card. */
export function closeness(p: Target, c: Cand, idf: (w: string) => number): { score: number; why: string[] } {
  let score = 0
  const why: string[] = []
  // "Vintage" says nothing about which piece it is (425 of the 432 vintage tees carry it, from $95 blanks to $2,500 grails), so it
  // earns no brand points: a vintage tee is matched by its band and model words, price and size (Tim, 5 Oct)
  if (!NO_BRAND.has(p.brand) && c.brand === p.brand) { score += 30; why.push('Same brand') }
  if (sameKind(p, c)) { score += 30; why.push(p.cat?.length && c.cat?.length ? 'Same category' : 'Same type') } else if (TOPS.includes(c.type) && TOPS.includes(p.type)) { score += 15; why.push('Also a top') }
  if (p.words.length && c.words.length) {
    const B = new Set(c.words)
    let sa = 0, sb = 0, sh = 0
    const shared: [string, number][] = []
    for (const w of p.words) { const v = idf(w); sa += v; if (B.has(w)) { sh += v; shared.push([w, v]) } }
    for (const w of c.words) sb += idf(w)
    const sim = sa && sb ? sh / Math.sqrt(sa * sb) : 0
    score += 25 * sim
    // the two rarest shared words, in the order the piece's title has them ("Gunslinger mules", not "Mules gunslinger")
    if (sim >= 0.12) why.push('“' + [...shared].sort((a, b) => b[1] - a[1]).slice(0, 2).sort((a, b) => p.words.indexOf(a[0]) - p.words.indexOf(b[0])).map(([w], i) => i ? w : pretty(w)).join(' ') + '”')
  }
  const ref = c.list ?? c.price
  if (p.anchor && ref) {
    const near = Math.exp(-Math.abs(Math.log(ref / p.anchor)) / 0.35)
    score += 10 * near
    if (near >= 0.6) why.push('Similar price')
  }
  if (SIZED.has(p.type) && p.size && c.size && p.size === c.size) { score += 5; why.push('Size ' + c.size) }
  if (p.condition && c.condition && p.condition === c.condition) score += 3
  return { score: Math.min(100, Math.round(score)), why }
}

/** Every candidate scored, closest first, with its weight: closeness squared, a year's age halving it, market checks at the set share. */
export function scoreAll(p: Target, cands: Cand[], idf: (w: string) => number, today: string, marketPct: number): Scored[] {
  const day = (d: string) => Date.parse(d + 'T00:00:00Z') / 86400000
  const t0 = day(today)
  return cands.filter((c) => c.pid !== p.pid).map((c) => {
    const { score, why } = closeness(p, c, idf)
    const age = c.when ? Math.max(0, t0 - day(c.when)) : 0
    const mk = c.kind === 'market' ? (marketPct / 100) * (c.mkind === 'asking' ? 0.5 : 1) : 1
    return { ...c, score, why, w: (score / 100) ** 2 * Math.pow(0.5, age / 365) * mk }
  }).sort((a, b) => b.score - a.score)
}

/** Weighted median of [value, weight] pairs. */
export function wmedian(xs: [number, number][]): number | null {
  const s = xs.filter(([v, w]) => Number.isFinite(v) && w > 0).sort((a, b) => a[0] - b[0])
  const tot = s.reduce((a, [, w]) => a + w, 0)
  if (!s.length || tot <= 0) return null
  let acc = 0
  for (const [v, w] of s) { acc += w; if (acc >= tot / 2) return v }
  return s[s.length - 1][0]
}

/** Days to sell from sold and unsold pieces together, weighted, with late entry (same as the engine's km in _shared/profit.ts). */
export interface Obs { t: number; e: boolean; entry: number; w?: number }
export function km(obs: Obs[]) {
  const times = [...new Set(obs.filter((o) => o.e).map((o) => o.t))].sort((a, b) => a - b)
  let S = 1
  const out: { p25: number | null; med: number | null; p75: number | null } = { p25: null, med: null, p75: null }
  for (const t of times) {
    let risk = 0, died = 0
    for (const o of obs) { const w = o.w ?? 1; if (o.entry < t && t <= o.t) { risk += w; if (o.e && o.t === t) died += w } }
    if (risk <= 0 || died <= 0) continue
    S *= 1 - died / risk
    if (out.p25 == null && S <= 0.75) out.p25 = t
    if (out.med == null && S <= 0.5) out.med = t
    if (out.p75 == null && S <= 0.25) { out.p75 = t; break }
  }
  return out
}

/** "5 to 12 days", "3 to 9 weeks", "2 to 5 months"; an open end reads "or longer". */
export function dur(d: number) {
  const r = Math.max(1, Math.round(d))
  return r < 14 ? `${r} day${r === 1 ? '' : 's'}` : r < 75 ? `${Math.round(r / 7)} weeks` : `${Math.round(r / 30.4)} months`
}
export function span(lo: number | null, hi: number | null) {
  if (lo == null) return 'not known yet'
  if (hi == null) return `${dur(lo)} or longer`
  const a = dur(lo), b = dur(hi)
  const ua = a.split(' ')[1].replace(/s$/, ''), ub = b.split(' ')[1].replace(/s$/, '')
  return a === b ? `about ${a}` : ua === ub ? `${a.split(' ')[0]} to ${b}` : `${a} to ${b}`
}
